/**
 * applyReportDirective — the report.* / actions sub-switch of core (B).
 *
 * Lifted VERBATIM from the ui-action branch of useChat.sendMessage's SSE loop
 * (useChat.ts L1135-L1343, pre-decomposition). Handles `report.init`,
 * `actions`, `report.status`, `report.section`. Every read/write of a turn-local
 * goes through `ctx.<field>`; every stable dep through `deps.<field>`.
 *
 * Behavioral notes preserved byte-for-byte:
 *   - report.init dedup returns early (was a `continue` in the inline loop —
 *     equivalent here because the switch was the tail of the loop body).
 *   - the AbortError mid-create path re-throws so runChatStream's try/catch
 *     handles cleanup (was `throw err` propagating to sendMessage's catch).
 *
 * Pinned by useChat.ui-directive-char.test.ts (report.init dedup + status/section).
 */

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query/keys'
import { findDuplicateReportId } from './helpers/report-dedup'
import type { ReportConfig, ReportSection as ReportSectionType } from '@/types/report'
import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { QuickAction } from './useChat.types'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

type UiAction = { type: string; [key: string]: unknown }

/**
 * Apply a report.* or `actions` directive. Returns true when the action was a
 * report/actions directive (so the caller knows it was handled), false when the
 * action type is not in this sub-switch (the html applier should try it).
 */
export async function applyReportDirective(
  ctx: TurnContext,
  action: UiAction,
  deps: ChatStreamDeps,
  signal: AbortSignal,
): Promise<boolean> {
  const { token, workspaceId, queryClient, isTargetConversationActive } = deps

  if (action.type === 'report.init') {
    // Create report module via API and focus it in the side panel
    const reportTheme = (typeof action.theme === 'string' ? action.theme : 'formal') as ReportConfig['theme']
    const reportTitle = typeof action.title === 'string' ? action.title
      : typeof action.label === 'string' ? action.label : 'Report'
    // Cross-path dedup (#1818): the agent's report-builder loop
    // occasionally fires a duplicate report.init after the first
    // build completes; ChatBox.tsx ALSO has a report.init handler
    // for the non-streaming code path. Without coordination, each
    // re-fire spawned a fresh module. We dedupe against TWO
    // sources of truth:
    //   1) the per-turn activeReportModules map (catches dupes
    //      within a single SSE stream before the cache catches
    //      up), and
    //   2) the workspace modules query cache (catches dupes
    //      across the useChat / ChatBox handler split, including
    //      modules just persisted by the other path).
    // Pure title-dedup (against the per-turn active map + the cached
    // modules list) extracted to helpers/report-dedup.ts (Pass-2). The
    // cache read, the `continue`, and the console.info stay here.
    const cachedModulesForDedup = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
    const dupOfId = findDuplicateReportId(reportTitle, ctx.activeReportModules, cachedModulesForDedup)
    if (dupOfId) {
      console.info('[useChat] report.init: duplicate of existing report, ignoring', { dupOfId, reportTitle })
      return true
    }
    const initialConfig: ReportConfig = {
      theme: reportTheme,
      title: reportTitle,
      status: 'building',
      statusMessage: 'Initializing report...',
      sections: [],
    }
    // Look up the workspace's Pages module so the report is parented
    // under it (mirrors the html.init flow). Server falls back to
    // auto-parent if we don't pass it.
    let reportParentId: string | undefined
    try {
      const cachedModules = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
      if (Array.isArray(cachedModules)) {
        const pagesModule = cachedModules.find(
          (m: any) => m?.type === 'pages' && !m?.archived && !m?.archivedAt,
        )
        if (pagesModule?.id) reportParentId = pagesModule.id
      }
    } catch (lookupErr) {
      console.warn('[useChat] Pages module lookup failed for report:', lookupErr)
    }
    try {
      // The server generates a moduleId in the report.init directive
      // and includes it as `moduleId`. We pass it as the explicit `id`
      // so the build registry, the disconnect/safety-net persists,
      // and the user-visible row all target the same DB row. Older
      // agents that don't emit moduleId fall back to API-generated
      // UUID. Mirrors the html.init flow.
      const serverReportModuleId = typeof (action as { moduleId?: unknown }).moduleId === 'string'
        ? (action as { moduleId: string }).moduleId
        : undefined
      const createdModule = await api.workspaces.createWorkspaceModule(token, workspaceId, {
        ...(serverReportModuleId ? { id: serverReportModuleId } : {}),
        type: 'report',
        label: reportTitle,
        position: -1000, // Place at top of sidebar (sorted ascending by position)
        ...(reportParentId ? { parentId: reportParentId } : {}),
        config: initialConfig as unknown as Record<string, unknown>,
      })
      // If superseded mid-create, delete the orphan and rethrow as
      // AbortError so the outer catch handles cleanup.
      if (signal.aborted) {
        try {
          await api.workspaces.deleteWorkspaceModule(token, workspaceId, createdModule.id)
        } catch (delErr) {
          console.error('[useChat] Failed to delete orphan report after abort:', delErr)
        }
        throw new DOMException('Aborted', 'AbortError')
      }
      ctx.activeReportModules.set(createdModule.id, { moduleId: createdModule.id, config: { ...initialConfig }, focused: false })
      // Refresh module catalog so the sidebar shows the new module.
      // Note: side-panel focus is deferred until the first report.section
      // arrives so the user doesn't stare at a blank panel.
      deps.onToolActivity?.()
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') throw err
      console.error('[useChat] Failed to create report module:', err)
    }
    return true
  } else if (action.type === 'actions') {
    // Quick action buttons sent via ui_action show_actions tool or text directives
    const actionsList = action.actions as QuickAction[] | undefined
    if (Array.isArray(actionsList)) {
      deps.queuedQuickActions.push(...actionsList)
    }
    return true
  } else if (action.type === 'report.status') {
    // Update status message on all active report modules
    const statusMessage = typeof action.message === 'string' ? action.message : undefined
    const targetModuleId = typeof action.moduleId === 'string' ? action.moduleId : undefined
    // Mirror the report status into the chat thinking indicator so both
    // the report panel and chat bubble show the same detailed message.
    if (statusMessage && isTargetConversationActive()) {
      deps.markXaviorStatusActivity()
      deps.setThinking(statusMessage)
      deps.pushThinkingStatus(statusMessage)
    }
    for (const [id, report] of ctx.activeReportModules) {
      if (targetModuleId && id !== targetModuleId) continue
      report.config.statusMessage = statusMessage
      // Push live config to the ReportModule component immediately
      window.dispatchEvent(new CustomEvent('xavior:report-config-update', {
        detail: { moduleId: id, config: { ...report.config } },
      }))
      try {
        await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
          config: report.config as unknown as Record<string, unknown>,
        })
        deps.onToolActivity?.()
      } catch (err) {
        console.error('[useChat] Failed to update report status:', err)
      }
    }
    return true
  } else if (action.type === 'report.section') {
    // Add or replace a section in the active report
    const sectionData = action.section as ReportSectionType | undefined
    const targetModuleId = typeof action.moduleId === 'string' ? action.moduleId : undefined
    if (sectionData && typeof sectionData.id === 'string') {
      for (const [id, report] of ctx.activeReportModules) {
        if (targetModuleId && id !== targetModuleId) continue
        // Add or replace section by id
        const existingIdx = report.config.sections.findIndex(s => s.id === sectionData.id)
        const section = { ...sectionData, visible: true }
        if (existingIdx >= 0) {
          report.config.sections[existingIdx] = section
        } else {
          report.config.sections.push(section)
        }
        // Push live config to the ReportModule component immediately
        window.dispatchEvent(new CustomEvent('xavior:report-config-update', {
          detail: { moduleId: id, config: { ...report.config, sections: [...report.config.sections] } },
        }))
        // First real section — open the side panel now (was deferred from report.init).
        //
        // Focus the parent Pages container (not the report itself) so the
        // user lands inside the Pages chrome — which provides the back
        // arrow and the rest of the list. We then tell PagesContainerModule
        // to auto-select this report as its current child via a custom
        // event. Without this, focusing the report module directly left
        // the user with no back-button (the X button closes the whole
        // chat pane), and they had to navigate away+back to recover the
        // Pages list.
        if (!report.focused) {
          report.focused = true
          const parentPagesId =
            typeof (report.config as any)?.__parentPagesId === 'string'
              ? (report.config as any).__parentPagesId
              : (() => {
                  // Look up the workspace's Pages container module from the
                  // React Query cache (same lookup we did at create time).
                  const cached = queryClient.getQueryData<any[]>(queryKeys.modules.list(workspaceId))
                  if (Array.isArray(cached)) {
                    const pagesModule = cached.find(
                      (m: any) => m?.type === 'pages' && !m?.archived && !m?.archivedAt,
                    )
                    if (pagesModule?.id) return pagesModule.id as string
                  }
                  return undefined
                })()
          if (parentPagesId) {
            // Stash the pending child request BEFORE we trigger
            // the focus. PagesContainerModule reads this on mount
            // — the dispatch alone races with React's mount cycle
            // (focus → mount → registerListener) and the event
            // can land before the listener exists.
            try {
              window.sessionStorage.setItem(
                `xavior:pages-pending-child:${parentPagesId}`,
                JSON.stringify({ childModuleId: id, childSource: 'module', ts: Date.now() }),
              )
            } catch { /* sessionStorage unavailable */ }
            deps.onUiActions?.([{
              type: 'module.focus',
              moduleId: parentPagesId,
              anchorMessageId: deps.assistantId,
              skipInvalidation: true,
            } as ChatUIAction])
            // Also dispatch the live event in case the listener
            // is already mounted (subsequent builds in the same
            // session, where the Pages container stayed open).
            window.dispatchEvent(new CustomEvent('xavior:pages-open-child', {
              detail: { parentPagesId, childModuleId: id, childSource: 'module' },
            }))
          } else {
            // Fallback: focus the report module directly (legacy behavior).
            // Better than nothing if the parent lookup somehow misses.
            deps.onUiActions?.([{
              type: 'module.focus',
              moduleId: id,
              anchorMessageId: deps.assistantId,
              skipInvalidation: true,
            } as ChatUIAction])
          }
        }
        try {
          await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
            config: report.config as unknown as Record<string, unknown>,
          })
          deps.onToolActivity?.()
        } catch (err) {
          console.error('[useChat] Failed to update report section:', err)
        }
      }
    }
    return true
  }

  return false
}
